import { Link } from 'react-router-dom';
import { CAPTURE_PRIMARY_ACTIONS, groupHomeInbox } from '@packproof/ux';
import type { IncomingDirectInvitation, PortalTransaction } from './api';
import { workspaceOf } from './workspace';

export function TaskCard({ item }: { item: PortalTransaction; viewerId: string }) {
  const workspace = workspaceOf(item);
  const next = workspace.nextAction;
  const captureOnPhone = Boolean(next.primaryAction && CAPTURE_PRIMARY_ACTIONS.has(next.primaryAction.kind));
  const proofAvailable = workspace.proof.availability === 'AVAILABLE';
  const proofIsPrimary = proofAvailable && !captureOnPhone;
  const meta = [item.source?.platform, item.source?.externalOrderId ? `Order ${item.source.externalOrderId}` : null]
    .filter(Boolean)
    .join(' • ');
  return (
    <article className="card">
      <p className="eyebrow">{next.humanStateLabel}</p>
      <h2>{item.title}</h2>
      <p className="meta">{meta || next.inboxSentence}</p>
      <p>{proofIsPrimary ? 'View Proof' : next.headline}</p>
      <div className="row" style={{ marginTop: 12 }}>
        {captureOnPhone ? <Link className="btn" to={`/packproofs/${item.id}/handoff`}>Continue on phone</Link> : null}
        {proofAvailable ? (
          <Link className={proofIsPrimary ? 'btn' : 'btn secondary'} to={`/packproofs/${item.id}/proof`}>View Proof</Link>
        ) : null}
        <Link className="btn ghost" to={`/packproofs/${item.id}`}>{proofIsPrimary ? 'Details' : 'Open'}</Link>
      </div>
    </article>
  );
}

export function HomeQueue({
  items,
  viewerId,
  allowEmpty = true,
}: {
  items: PortalTransaction[];
  viewerId: string;
  allowEmpty?: boolean;
}) {
  const grouped = groupHomeInbox(items, (item) => workspaceOf(item).nextAction);
  if (!grouped.needsAttention.length && !grouped.waiting.length) {
    if (!allowEmpty) return null;
    return (
      <div className="card">
        <h2>Nothing needs you</h2>
        <p className="lede">When a PackProof needs packing, review, or a decision, it will show up here.</p>
      </div>
    );
  }
  return (
    <div className="stack">
      {grouped.needsAttention.map((item) => <TaskCard key={item.id} item={item} viewerId={viewerId} />)}
      {grouped.waiting.map((item) => <TaskCard key={item.id} item={item} viewerId={viewerId} />)}
    </div>
  );
}

function formatInvitePrice(priceMinor: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(priceMinor / 100);
  } catch {
    return `${currency} ${(priceMinor / 100).toFixed(2)}`;
  }
}

export function IncomingInviteQueue({
  items,
  busyId,
  onAccept,
  onDefer,
}: {
  items: IncomingDirectInvitation[];
  busyId: string | null;
  onAccept: (invitationId: string) => void;
  onDefer: (invitationId: string) => void;
}) {
  if (!items.length) return null;
  return (
    <div className="stack">
      <p className="eyebrow">Incoming PackProof</p>
      {items.map((item) => (
        <article className="card" key={item.invitationId}>
          <p className="eyebrow">Invitation</p>
          <h2>{item.title}</h2>
          <p className="meta">{[item.sellerDisplayName, item.category, formatInvitePrice(item.priceMinor, item.currency)].filter(Boolean).join(' • ')}</p>
          <p>Someone sent you a PackProof. Accepting joins you as the buyer so you can review the terms.</p>
          <div className="row" style={{ marginTop: 12 }}>
            <button className="btn" type="button" disabled={busyId === item.invitationId} onClick={() => onAccept(item.invitationId)}>
              Review / Accept
            </button>
            <button className="btn secondary" type="button" disabled={busyId === item.invitationId} onClick={() => onDefer(item.invitationId)}>
              Not now
            </button>
          </div>
        </article>
      ))}
    </div>
  );
}
